/**
 * SolicitudesDocCard — Tarjeta sidebar del expediente para gestionar las
 * "Solicitudes de documentación" creadas por los peritos internos.
 *
 * Visibilidad y permisos:
 * - Si el usuario es perito_interno → ve el botón "+ Solicitar" para crearlas.
 * - Si el usuario es colector → ve los botones "Coger" / "Entregar" / "Liberar".
 * - Cualquier usuario con acceso al expediente puede consultarla.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Plus, ClipboardText, Paperclip, ArrowsClockwise, CheckCircle, X, Clock,
  UserCircle, Wrench, Users, FileText,
} from "@phosphor-icons/react";
import SolicitudDocCreateModal from "@/components/expediente/SolicitudDocCreateModal";

const STATUS_BADGE = {
  pendiente: { label: "Pendiente", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  en_curso:  { label: "En curso",  cls: "bg-blue-100 text-blue-800 border-blue-200" },
  entregada: { label: "Entregada", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelada: { label: "Cancelada", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" },
};

const TARGET_ICON = {
  asegurado: UserCircle,
  perjudicado: Users,
  reparador: Wrench,
};

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("es-ES", {
    day: "2-digit", month: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

function fmtBytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

function DeliverInline({ solicitud, onDone }) {
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (files.length === 0) {
      toast.error("Adjunta al menos un archivo");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (note.trim()) fd.append("note", note.trim());
      await api.post(`/solicitudes-doc/${solicitud.id}/deliver`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Entregado");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al entregar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 border-t border-border pt-2 space-y-1.5" data-testid={`solicitud-deliver-${solicitud.id}`}>
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className="w-full text-[11px] inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md border border-dashed border-border hover:border-brand hover:bg-brand/5 text-foreground"
        data-testid={`solicitud-deliver-pick-${solicitud.id}`}
      >
        <Paperclip className="w-3 h-3" weight="bold" /> {files.length ? `${files.length} archivo${files.length === 1 ? "" : "s"}` : "Adjuntar archivos"}
      </button>
      <input
        ref={fileRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          setFiles(Array.from(e.target.files || []));
          e.target.value = "";
        }}
      />
      {files.length > 0 && (
        <ul className="text-[10.5px] text-muted space-y-0.5 max-h-20 overflow-y-auto">
          {files.map((f) => (
            <li key={`${f.name}-${f.size}-${f.lastModified}`} className="truncate">• {f.name} <span className="text-zinc-400">({fmtBytes(f.size)})</span></li>
          ))}
        </ul>
      )}
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        placeholder="Nota (opcional)"
        className="!text-[11px] w-full"
      />
      <button
        onClick={submit}
        disabled={busy || files.length === 0}
        className="w-full text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
        data-testid={`solicitud-deliver-submit-${solicitud.id}`}
      >
        <CheckCircle className="w-3.5 h-3.5" weight="bold" /> {busy ? "Entregando…" : "Entregar"}
      </button>
    </div>
  );
}

function SolicitudRow({ s, isColector, isOwner, onAction }) {
  const Icon = TARGET_ICON[s.target_type] || ClipboardText;
  const badge = STATUS_BADGE[s.status] || STATUS_BADGE.pendiente;
  const [deliverOpen, setDeliverOpen] = useState(false);

  const take = async () => {
    try {
      await api.post(`/solicitudes-doc/${s.id}/take`);
      toast.success("La has cogido");
      onAction?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo coger");
    }
  };
  const release = async () => {
    try {
      await api.post(`/solicitudes-doc/${s.id}/release`);
      toast("Liberada");
      onAction?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo liberar");
    }
  };
  const cancel = async () => {
    if (!window.confirm("¿Cancelar esta solicitud?")) return;
    try {
      await api.post(`/solicitudes-doc/${s.id}/cancel`);
      toast("Cancelada");
      onAction?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cancelar");
    }
  };

  const canTake = isColector && s.status === "pendiente";
  const canRelease = isColector && s.status === "en_curso" && s.taken_by;
  const canDeliver = isColector && (s.status === "pendiente" || s.status === "en_curso");
  const canCancel = (isOwner || isColector) && (s.status === "pendiente" || s.status === "en_curso");

  return (
    <li className="text-[12px] rounded-md border border-border bg-white px-2.5 py-2 space-y-1.5" data-testid={`solicitud-row-${s.id}`}>
      <div className="flex items-start gap-1.5">
        <Icon className="w-3.5 h-3.5 text-brand flex-shrink-0 mt-0.5" weight="duotone" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold text-foreground">{s.target_label || s.target_type}</span>
            <span className={`text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-px rounded border ${badge.cls}`}>
              {badge.label}
            </span>
          </div>
          <div className="text-[11px] text-foreground/90 whitespace-pre-line break-words mt-0.5">
            {s.descripcion}
          </div>
          <div className="text-[10px] text-muted mt-1 flex items-center gap-1.5 flex-wrap">
            <Clock className="w-2.5 h-2.5" weight="bold" />
            {fmtDate(s.created_at)}
            {s.created_by_name && <>· por {s.created_by_code || s.created_by_name}</>}
            {s.taken_by_name && <>· tomada por {s.taken_by_name}</>}
          </div>
        </div>
      </div>

      {s.delivered_files && s.delivered_files.length > 0 && (
        <ul className="ml-5 space-y-0.5" data-testid={`solicitud-files-${s.id}`}>
          {s.delivered_files.map((f) => (
            <li key={f.attachment_id} className="text-[10.5px] text-muted inline-flex items-center gap-1">
              <FileText className="w-2.5 h-2.5" weight="bold" />
              <a
                href={`${API}/solicitudes-doc/files/${f.attachment_id}`}
                target="_blank"
                rel="noreferrer"
                className="text-brand hover:underline truncate max-w-[160px] inline-block align-bottom"
                title={f.filename}
              >
                {f.filename}
              </a>
              <span className="text-zinc-400">({fmtBytes(f.size_bytes)})</span>
            </li>
          ))}
        </ul>
      )}
      {s.delivered_note && (
        <div className="ml-5 text-[10.5px] text-muted italic">"{s.delivered_note}"</div>
      )}

      {(canTake || canRelease || canCancel || canDeliver) && !deliverOpen && (
        <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
          {canTake && (
            <button onClick={take} className="text-[10.5px] font-semibold px-2 py-1 rounded bg-foreground text-white" data-testid={`solicitud-take-${s.id}`}>
              Cogerla
            </button>
          )}
          {canDeliver && (
            <button onClick={() => setDeliverOpen(true)} className="text-[10.5px] font-semibold px-2 py-1 rounded bg-emerald-600 text-white" data-testid={`solicitud-open-deliver-${s.id}`}>
              Entregar
            </button>
          )}
          {canRelease && (
            <button onClick={release} className="text-[10.5px] font-semibold px-2 py-1 rounded border border-border text-muted hover:text-foreground" data-testid={`solicitud-release-${s.id}`}>
              Liberar
            </button>
          )}
          {canCancel && (
            <button onClick={cancel} className="text-[10.5px] font-semibold px-2 py-1 rounded text-muted hover:text-status-error-fg inline-flex items-center gap-1" data-testid={`solicitud-cancel-${s.id}`}>
              <X className="w-2.5 h-2.5" weight="bold" /> Cancelar
            </button>
          )}
        </div>
      )}
      {deliverOpen && (
        <DeliverInline solicitud={s} onDone={() => { setDeliverOpen(false); onAction?.(); }} />
      )}
    </li>
  );
}

export default function SolicitudesDocCard({ expediente }) {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, open: 0 });
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const isPeritoInterno = user?.role === "perito_interno" || user?.role === "admin";
  const isColector = user?.is_colector || user?.role === "admin";

  const load = useCallback(async () => {
    if (!expediente?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/expedientes/${expediente.id}/solicitudes-doc`);
      setData(r.data || { items: [], total: 0, open: 0 });
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.debug("[solicitudes] load:", e);
    } finally {
      setLoading(false);
    }
  }, [expediente?.id]);

  useEffect(() => { load(); }, [load]);

  // Si no es perito_interno y no hay solicitudes, ocultamos la tarjeta.
  if (!isPeritoInterno && data.total === 0 && !loading) return null;

  return (
    <section
      className="bg-white border border-border rounded-lg overflow-hidden"
      data-testid="solicitudes-doc-card"
    >
      <header className="flex items-center gap-2 px-4 py-2.5 border-b border-border/70">
        <span className="icon-chip chip-icon-blue flex-shrink-0">
          <ClipboardText className="w-4 h-4" weight="regular" />
        </span>
        <h3 className="section-title flex-1">Solicitudes de documentación</h3>
        {data.open > 0 && (
          <span
            className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
            data-testid="solicitudes-doc-open-badge"
          >
            {data.open} abierta{data.open === 1 ? "" : "s"}
          </span>
        )}
        <button
          onClick={load}
          title="Refrescar"
          className="text-muted hover:text-foreground"
          data-testid="solicitudes-doc-refresh"
        >
          <ArrowsClockwise className="w-3.5 h-3.5" weight="bold" />
        </button>
      </header>

      <div className="p-3 space-y-2">
        {isPeritoInterno && (
          <button
            onClick={() => setCreating(true)}
            data-testid="solicitudes-doc-create-btn"
            className="w-full inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold px-2.5 py-1.5 rounded-md bg-brand text-white hover:bg-brand/90"
          >
            <Plus className="w-3.5 h-3.5" weight="bold" /> Solicitar documentación
          </button>
        )}

        {loading && data.items.length === 0 && (
          <div className="text-[12px] text-muted text-center py-2">Cargando…</div>
        )}

        {!loading && data.items.length === 0 && (
          <div className="text-[11.5px] text-muted text-center py-2 italic">
            Aún no hay solicitudes.
          </div>
        )}

        {data.items.length > 0 && (
          <ul className="space-y-1.5" data-testid="solicitudes-doc-list">
            {data.items.map((s) => (
              <SolicitudRow
                key={s.id}
                s={s}
                isColector={isColector}
                isOwner={user?.id === s.created_by}
                onAction={load}
              />
            ))}
          </ul>
        )}
      </div>

      {creating && (
        <SolicitudDocCreateModal
          expediente={expediente}
          onClose={() => setCreating(false)}
          onCreated={load}
        />
      )}
    </section>
  );
}
